import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Call } from "@mui/icons-material";
import axios from "axios";
import { useState } from "react";
import PhoneInput from "react-phone-input-2";
import logo from "../../assets/logo.png";

// Agent created by backend/tripemco-demo/scripts/setup_tripemco_demo.py
// (ids persisted in backend/tripemco-demo/config/tripemco_demo_config.json).
export const AGENT_ID = "agent_cad2c5fca39c9effe62d0723be";
export const FROM_NUMBER = "15489185087"; // Ontario (548) SIP number bound to Retell
export const BASE_URL =
  "https://initiateretellcall-655075094783.us-central1.run.app";

// Fictional sample client matching Tripemco's ideal profile.
// Every key is forwarded to the agent as a Retell dynamic variable.
export const DEMO_VARIABLES = {
  business_name: "Northline Contracting",
  industry: "construction",
  employee_count_hint: "around 20",
  broker_years: "several years",
  advisor_name: "Saba Aliakbari",
  slot_1: "Tuesday at 10:30 a.m.",
  slot_2: "Thursday at 2:00 p.m.",
  callback_office: "(905) 664-2266",
};

export const SCENARIOS = {
  owner_direct: {
    label: "Business owner answers",
    description:
      "The standard call: a long-time Tripemco commercial client picks up their cell.",
  },
  receptionist: {
    label: "Receptionist answers first",
    description:
      "Answer as the front desk. Try asking “what's this regarding?”, taking a message, or putting the call through.",
  },
  stress: {
    label: "Stress test — try to break it",
    description: "Same call, but come at it hard. Some ideas:",
    chips: [
      "Ask what it costs",
      "Ask if it covers dental",
      "Interrupt it mid-sentence",
      "Give a long rambling answer",
      "Ask about your commercial policy instead",
      "Say you're happy with your current plan",
    ],
  },
  voicemail: {
    label: "Voicemail",
    description:
      "Answer like a voicemail greeting (“You've reached… leave a message”) and hear the message it leaves.",
  },
};

const CAPTURED_FIELDS =
  "outcome, employee count, plan status, plan friction, renewal month, booked slot and any cross-sell note";

export const TripemcoDemo = () => {
  const [scenario, setScenario] = useState("owner_direct");
  const [contactName, setContactName] = useState("Saba");
  const [phoneNumber, setPhoneNumber] = useState("16478857176");
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"

  const current = SCENARIOS[scenario];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");

    try {
      await axios.get(BASE_URL, {
        params: {
          agent_id: AGENT_ID,
          from_number: FROM_NUMBER,
          to_number: phoneNumber,
          scenario,
          contact_name: contactName.trim() || "Saba",
          ...DEMO_VARIABLES,
        },
      });
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <div className="full-page-background">
      <Container className="App" maxWidth="sm">
        <img src={logo} className="App-logo" alt="logo" />
        <h1>Tripemco Financial Services — Group Benefits Assistant</h1>
        <h3>
          Demo by JasperVOCAL · Pick a scenario, enter your number, and Jess
          will call you.
        </h3>

        <form onSubmit={handleSubmit}>
          {/* Scenario selector */}
          <FormControl fullWidth margin="normal">
            <Select
              value={scenario}
              onChange={(e) => {
                setScenario(e.target.value);
                setStatus(null);
              }}
              sx={{ backgroundColor: "white", borderRadius: "4px" }}
            >
              {Object.entries(SCENARIOS).map(([key, s]) => (
                <MenuItem key={key} value={key}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.8)",
              mb: 1,
              mt: 0.5,
              fontStyle: "italic",
            }}
          >
            {current.description}
          </Typography>

          {current.chips && (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              sx={{ mb: 1 }}
            >
              {current.chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  size="small"
                  variant="outlined"
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
                />
              ))}
            </Stack>
          )}

          {/* Name */}
          <TextField
            label="Your name"
            variant="filled"
            sx={{ backgroundColor: "white", borderRadius: "4px" }}
            fullWidth
            margin="normal"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
          />

          {/* Phone number */}
          <Box sx={{ mt: 2 }}>
            <PhoneInput
              country="ca"
              value={phoneNumber}
              disableDropdown={true}
              disableCountryGuess={true}
              onChange={setPhoneNumber}
              enableLongNumbers={true}
              inputProps={{ required: true, "aria-label": "Phone number" }}
              inputStyle={{
                width: "100%",
                textAlign: "center",
                fontSize: "16px",
                height: "56px",
                borderRadius: "4px",
                border: "1px solid #ced4da",
              }}
            />
          </Box>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.9)", mt: 2, mb: 2 }}
          >
            You will receive a call from: <b>+1 (548) 918-5087</b>
          </Typography>

          {status === "success" && (
            <Typography sx={{ color: "#4caf50", mb: 1, fontWeight: "bold" }}>
              ✓ Jess is calling you now. Pick up and play the scenario.
            </Typography>
          )}
          {status === "error" && (
            <Typography sx={{ color: "#f44336", mb: 1, fontWeight: "bold" }}>
              ✗ Could not start the call. Please try again.
            </Typography>
          )}

          <Button
            className="callBtn"
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={status === "loading"}
          >
            <h2 style={{ fontSize: "16px" }}>
              {status === "loading" ? "Calling…" : "Call me now"}
            </h2>
            <div style={{ width: "20px" }} />
            <Call className="callIcon" />
          </Button>
        </form>

        <Typography
          variant="caption"
          sx={{ display: "block", color: "rgba(255,255,255,0.7)", mt: 3 }}
        >
          On every call Jess records the {CAPTURED_FIELDS}. In production these
          fields sync to your CRM automatically.
        </Typography>
      </Container>
    </div>
  );
};
